import { Paper, Stack, TextField } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import CustomButton from '@/components/inputs/CustomButton'
import PageHeader from '@/components/layouts/PageHeader'
import { useAlertContext } from '@/contexts/AlertContext'
import { PrequalificationRouteInformation } from '@/features/prequalification/constants'
import AddResourcesPanel from '@/features/prequalification/localControl/components/AddResourcesPanel'
import ResourceSetResourceBasket from '@/features/prequalification/localControl/components/ResourceSetResourceBasket'
import { useCreateLocalPqResourceSetMutation } from '@/features/prequalification/localControl/hooks/useCreateLocalPqResourceSetMutation'
import { useInvalidateLocalPqResourceSetsQuery } from '@/features/prequalification/localControl/hooks/useLocalPqResourceSetsQuery'
import { useResourceBasket } from '@/features/prequalification/localControl/hooks/useResourceBasket'
import { errorHandler } from '@/utils/errorHandler'

/**
 * Two-pane resource set builder: name the resource set and assemble its resources (no test,
 * no schedule) from the right-hand panel into the basket, then create the resource set.
 * The resource set name is the resource set's identity. Tests are triggered later from the
 * detail page, so creation just persists name + resources and redirects.
 */
function NewPrequalificationResourceSet() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pushAlert } = useAlertContext()

  const { createResourceSet, isPending } = useCreateLocalPqResourceSetMutation()
  const invalidateResourceSets = useInvalidateLocalPqResourceSetsQuery()

  const [name, setName] = useState('')
  const { resources, selectedIds, toggleResource, removeResource } = useResourceBasket()

  async function handleCreate() {
    if (resources.length === 0) return

    try {
      const { id: resourceSetId } = await createResourceSet({
        name: name.trim() || null,
        resourceIds: resources.map((resource) => resource.resourceId),
      })
      await invalidateResourceSets()
      pushAlert({ message: t('prequalifications.resource_set.new.create_success'), severity: 'success' })
      navigate(PrequalificationRouteInformation.RESOURCE_SET_DETAILS.navigationPath(resourceSetId))
    } catch (err) {
      const error = errorHandler(err, t('prequalifications.resource_set.new.create_error'))
      pushAlert({ message: error.message, severity: 'error' })
    }
  }

  return (
    <>
      <PageHeader
        hiddenTitle
        breadcrumbItems={[
          {
            text: t('component.page_header.prequalifications'),
            to: PrequalificationRouteInformation.INDEX.navigationPath(),
          },
          {
            text: t('prequalifications.scope.local_control'),
            to: PrequalificationRouteInformation.INDEX.navigationPath(),
          },
        ]}
        pageTag="prequalifications-new"
        pageTitle={t('prequalifications.resource_set.new.title')}
      />

      <Stack
        sx={{
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <CustomTypography noWrap sx={{ minWidth: 0 }} variant="h1">
          {t('prequalifications.resource_set.new.title')}
        </CustomTypography>

        <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          <CustomButton
            disabled={isPending}
            variant="outlined"
            onClick={() => navigate(PrequalificationRouteInformation.INDEX.navigationPath())}
          >
            {t('common.button.cancel')}
          </CustomButton>
          <CustomButton
            data-testid="create-resource-set-button"
            disabled={resources.length === 0 || isPending}
            variant="contained"
            onClick={handleCreate}
          >
            {t('prequalifications.resource_set.new.create_button', { count: resources.length })}
          </CustomButton>
        </Stack>
      </Stack>

      <Stack sx={{ flexDirection: { xs: 'column', md: 'row' }, gap: 3, mt: 3, alignItems: 'stretch' }}>
        <Paper sx={{ p: 3, flex: 1, minWidth: 0 }}>
          <Stack sx={{ gap: 3, height: '100%' }}>
            <TextField
              fullWidth
              helperText={t('prequalifications.resource_set.new.name_hint')}
              label={t('prequalifications.resource_set.new.name_label')}
              placeholder={t('prequalifications.resource_set.new.name_placeholder')}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            <ResourceSetResourceBasket resources={resources} onRemove={removeResource} />
          </Stack>
        </Paper>
        <Paper sx={{ p: 3, width: { xs: '100%', md: 400 }, flexShrink: 0 }}>
          <AddResourcesPanel selectedIds={selectedIds} onToggle={toggleResource} />
        </Paper>
      </Stack>
    </>
  )
}

export default NewPrequalificationResourceSet
